import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { of, Subject, Subscription, timer } from 'rxjs';
import { catchError, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { BackendCallService } from 'src/app/services/backend-call/backend-call.service';
import { EventProvider } from 'src/app/services/event';
import { ObservationEvents } from 'src/app/utilities/constant';

@Component({
  selector: 'app-machine-filter',
  templateUrl: './machine-filter.component.html',
  styleUrls: ['./machine-filter.component.scss'],
})
export class MachineFilterComponent implements OnInit, OnDestroy {
  private attributeCache: { [key: string]: any } = {};
  public attributes = [];
  public operatorList = [];

  public values = [
    { id: '', value: '' }
  ];
  public customerInput$ = new Subject<string>();
  public customerLoading = false;
  public customerSearchCompleted = false;
  private customerSearchTerm = '';
  private customerOffset = 0;
  private customerHasMore = true;
  private customerPageSubscription: Subscription;
  private destroy$ = new Subject<void>();
  private count = 100;
  private readonly customerCount = 20;
  private increasedApplicationCount = 0;
  public isApplicationSelected: boolean;
  public selectedApplication: string;
  public selectedOperator: string;
  public selectedValue: string;
  public filters: { field: string; operator: string; value: string; id: number}[];
  @Input() isFilterVisible = false;
  @Input() isOpendByFilterButton = false;
  public appliedFilterSubscription: any;
  public filterOpendByChip: boolean;
  public editFiltrList: any = {};
  private currentIndex = 0;
  private readonly attributeValueFieldMap: Record<string, string> = {
    applications: 'application',
    machinetypes: 'machine_type',
    specificfeatures: 'specific_feature',
    controlsystems: 'control_system'
  };

  constructor(private eventProvider: EventProvider,
    public backendService: BackendCallService,
    private translate: TranslateService) { }


  ngOnInit() {
    this.setTranslatedFilterOptions();
    this.subscribeCustomerSearch();

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.attributeCache = {};
      this.setTranslatedFilterOptions();

      if (this.selectedApplication) {
        this.values = [];
        this.getAttributes(this.selectedApplication);
      }
    });

    this.eventProvider.event(ObservationEvents.IS_OPENED_BY_BTN).pipe(takeUntil(this.destroy$)).subscribe((eventOpened) => {
      this.filterOpendByChip = eventOpened;
    });
    this.appliedFilterSubscription = this.eventProvider.event(ObservationEvents.APPLIED_FILTER).pipe(takeUntil(this.destroy$)).subscribe((fltr) => {
      const matchingAttribute = this.attributes.find(attr => attr.value === this.mapfieldToFilter(fltr.field));
      this.selectedApplication = matchingAttribute?.value;
      this.selectedOperator = fltr.operator;
      this.selectedValue = fltr.value;
      this.editFiltrList = fltr;

      if (this.selectedApplication) {
        this.values = [];
        this.getAttributes(this.selectedApplication);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.customerPageSubscription?.unsubscribe();
  }

  /**
   * Ares all fields filled
   *
   * @returns true if all fields filled
   */
  public areAllFieldsFilled(): boolean {
    return !!this.selectedApplication && !!this.selectedOperator && !!this.selectedValue;
  }

  /**
   * Determines whether application change on
   *
   * @param event event
   */
  public onApplicationChange(event: any) {
    const attributeType = this.normalizeSelectValue(event);

    this.selectedValue = null;
    this.selectedApplication = attributeType;
    this.values = [];

    if (attributeType) {
      this.getAttributes(attributeType);
    }
  }

  /**
   * Determines whether opratoer change on
   *
   * @param event event
   */
  public onOpratoerChange(event: any) {
    this.selectedOperator = this.normalizeSelectValue(event);
  }

  /**
   * Determines whether value change on
   *
   * @param event event
   */
  public onValueChange(event: any) {
    this.selectedValue = this.normalizeSelectValue(event);
  }

  /**
   * Cancels machine filter component
   */
  public cancel() {
    this.selectedApplication = null;
    this.selectedOperator = null;
    this.selectedValue = null;
    this.eventProvider.BroadcastEvent(ObservationEvents.CLOSE_FILTER_POPUP, false);
  }

  /**
   * Applys machine filter component
   */
  public apply() {
    const filterField = this.getFilterField(this.selectedApplication);
    if (!this.filterOpendByChip) {
      this.editFiltrList.field = filterField;
      this.editFiltrList.operator = this.selectedOperator;
      this.editFiltrList.value = this.selectedValue;
      this.eventProvider.BroadcastEvent(ObservationEvents.EDIT_FILTER, this.editFiltrList);
    } else {
      this.filters = [{
        field: filterField,
        operator: this.selectedOperator,
        value: this.selectedValue,
        id: this.currentIndex++
      }];
      this.eventProvider.BroadcastEvent(ObservationEvents.MACHINE_FILTER, this.filters);
    }
    this.selectedApplication = null;
    this.selectedOperator = null;
    this.selectedValue = null;
    this.eventProvider.BroadcastEvent(ObservationEvents.CLOSE_FILTER_POPUP, false);
  }

  /**
   * Gets attributes
   *
   * @param attributeType attributeType
   */
  private getAttributes(attributeType) {
    if (attributeType === 'customernames') {
      this.customerInput$.next('');
      return;
    }
    // Check if data for the given attribute type already exists in the cache
    if (this.attributeCache[attributeType]) {
      // Use the cached data
      this.values = this.attributeCache[attributeType];
    } else {
      // If data doesn't exist in the cache, make the API call
      const payload = {
        search: ' ',
        count: this.count,
        increasedCount: this.increasedApplicationCount,
      };

      this.backendService.getAttributes(payload, attributeType).subscribe((attribute: any) => {
        const attributeValueField = this.attributeValueFieldMap[attributeType];
        // Store the data in the cache
        this.attributeCache[attributeType] = (attribute?.value || [])
          .map(item => ({
            id: item?.id,
            value: this.resolveAttributeValue(item, attributeValueField),
          }))
          .filter(item => !!item.value);

        // Set the values variable with the retrieved data
        if (this.selectedApplication === attributeType) {
          this.values = this.attributeCache[attributeType];
        }
      });
    }
  }

  private subscribeCustomerSearch(): void {
    this.customerInput$.pipe(
      switchMap(searchTerm => {
        this.customerPageSubscription?.unsubscribe();
        this.customerSearchTerm = (searchTerm || '').trim();
        this.customerOffset = 0;
        this.customerHasMore = true;
        this.customerLoading = true;
        this.customerSearchCompleted = false;
        this.values = this.selectedValue ? [{ id: this.selectedValue, value: this.selectedValue }] : [];
        return timer(600).pipe(
          switchMap(() => this.backendService.searchMachineCustomers(this.customerPayload(0))),
          catchError(() => of({ response: [] })),
          finalize(() => { this.customerLoading = false; })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe((response: any) => {
      if (this.selectedApplication === 'customernames') {
        this.setCustomerValues(response, false);
        this.customerSearchCompleted = true;
      }
    });
  }

  public fetchMoreCustomers(): void {
    if (this.selectedApplication !== 'customernames' || this.customerLoading || !this.customerHasMore) {
      return;
    }

    const searchTerm = this.customerSearchTerm;
    const nextOffset = this.customerOffset + this.customerCount;
    this.customerLoading = true;
    this.customerPageSubscription = this.backendService.searchMachineCustomers(this.customerPayload(nextOffset)).pipe(
      catchError(() => of({ response: [] })),
      finalize(() => { this.customerLoading = false; })
    ).subscribe((response: any) => {
      if (this.selectedApplication === 'customernames' && searchTerm === this.customerSearchTerm) {
        this.customerOffset = nextOffset;
        this.setCustomerValues(response, true);
      }
    });
  }

  private customerPayload(increasedCount: number): {
    cloning_from_project: boolean; customer_id: string; customer_name: string; count: number; increasedCount: number
  } {
    return {
      cloning_from_project: false,
      customer_id: '',
      customer_name: this.customerSearchTerm,
      count: this.customerCount,
      increasedCount
    };
  }

  private setCustomerValues(response: any, append: boolean): void {
    const customers = response?.response || response?.value || [];
    this.customerHasMore = customers.length === this.customerCount;
    const names = new Set(append ? this.values.map(item => item.value) : this.selectedValue ? [this.selectedValue] : []);
    const values = append ? [...this.values] : this.selectedValue ? [{ id: this.selectedValue, value: this.selectedValue }] : [];

    customers.forEach(customer => {
      const name = this.resolveCustomerName(customer)?.trim();
      if (name && !names.has(name)) {
        names.add(name);
        values.push({ id: name, value: name });
      }
    });
    this.values = values;
  }

  private normalizeSelectValue(event: any): string {
    if (typeof event === 'string') {
      return event;
    }

    if (event && typeof event.value === 'string') {
      return event.value;
    }

    if (event && typeof event.label === 'string') {
      return event.label;
    }

    return '';
  }

  private setTranslatedFilterOptions(): void {
    this.attributes = [
      { key: 'Customer Name', value: 'customernames', label: this.translateWithFallback('Customer Name') },
      { key: 'Application', value: 'applications', label: this.translateWithFallback('Application') },
      { key: 'Machine Type', value: 'machinetypes', label: this.translateWithFallback('Machine Type') },
      { key: 'Specific Feature', value: 'specificfeatures', label: this.translateWithFallback('Specific Feature') },
      { key: 'Control System', value: 'controlsystems', label: this.translateWithFallback('Control System') }
    ];

    this.operatorList = [
      { value: 'equals', label: this.translateWithFallback('Equals') },
      { value: 'notequals', label: this.translateWithFallback('Does not equal') },
      { value: 'contains', label: this.translateWithFallback('Contains') },
      { value: 'doesn\'tcontain', label: this.translateWithFallback('Does not contain') },
      { value: 'startswith', label: this.translateWithFallback('Start with') },
      { value: 'doesn\'tstartwith', label: this.translateWithFallback('Does not start with') },
      { value: 'endswith', label: this.translateWithFallback('Ends With') },
      { value: 'doesn\'tendwith', label: this.translateWithFallback('Does not end with') }
    ];
  }

  private translateWithFallback(key: string): string {
    const translated = this.translate.instant(key);
    return translated && translated !== key ? translated : key;
  }

  private resolveAttributeValue(item: any, primaryField: string): string {
    const candidateFields = [
      primaryField,
      `${primaryField}_translated`,
      `${primaryField}_translation`,
      'name',
      'value'
    ];

    for (const field of candidateFields) {
      const value = item?.[field];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }

    const fallbackValue = Object.entries(item || {}).find(([key, value]) => {
      return key !== 'id' && typeof value === 'string' && value.trim();
    });

    return fallbackValue ? String(fallbackValue[1]) : '';
  }

  private resolveCustomerName(customer: any): string {
    return customer?.customer_name
      || customer?.customer_Name
      || customer?.Customer_Name
      || customer?.customerName
      || customer?.CustomerName
      || customer?.company_Name
      || customer?.company_name
      || customer?.companyName
      || customer?.name
      || '';
  }

  private getFilterField(attributeType: string): string {
    switch (attributeType) {
      case 'customernames':
        return 'customer_name';
      case 'applications':
        return 'application';
      case 'machinetypes':
        return 'machinetype';
      case 'specificfeatures':
        return 'specificfeature';
      case 'controlsystems':
        return 'controlsystem';
      default:
        return attributeType ? attributeType.slice(0, -1) : '';
    }
  }

  /**
   * Maps attribute to value
   *
   * @param attribute attribute
   * @returns attribute value
   */
  private mapfieldToFilter(field: string): string | undefined {
    switch (field) {
      case 'customer_name':
      case 'customer_Name':
      case 'customerName':
      case 'customername':
      case 'customernames':
        return 'customernames';
      case 'applications':
      case 'application':
        return 'applications';
      case 'machinetypes':
      case 'machinetype':
        return 'machinetypes';
      case 'specificfeatures':
      case 'specificfeature':
        return 'specificfeatures';
      case 'controlsystems':
      case 'controlsystem':
        return 'controlsystems';
      default:
        return undefined; // Handle the case where no mapping is found
    }
  }
}
